import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  APP_NAME,
  DEFAULT_PARTNER_SLUG,
  usePartnerStore,
  type PartnerConfig,
} from '@/stores/partner.store';

/**
 * The privacy policy and terms of service, at /privacy and /terms.
 *
 * Whose they are is the server's answer (`/api/v1/partner`): Faxter Ltd on
 * Faxter's own instance, the partner's company on theirs. The instance's
 * country picks the law — UK GDPR and England and Wales for GB, the Nigeria
 * Data Protection Act and Nigerian law otherwise. A partner that has not given
 * its company name, number and address gets the pages marked as a draft.
 *
 * A UK partner's instance shows the partner's own privacy policy (Zest I/O's,
 * as Clerk does on theirs), adapted to what Pulse does: see UkPartnerPrivacy.
 */

const UPDATED = '27 October 2026';
const UK_PARTNER_PRIVACY_UPDATED = '4 November 2026';

interface Entity {
  name: string;
  registration: string | null;
  address: string | null;
  icoNumber: string | null;
  email: string | null;
  complete: boolean;
  uk: boolean;
  /** A partner's instance, not Faxter's own. */
  partner: boolean;
  companyNumber: string | null;
  dpo: string | null;
  phone: string | null;
}

function entityFor(partner: PartnerConfig | null): Entity {
  const uk = (partner?.default_country ?? '').toUpperCase() === 'GB';
  if (!partner || partner.slug === DEFAULT_PARTNER_SLUG) {
    return {
      name: 'Faxter Ltd',
      registration: 'RC 1677701',
      address: 'Suite 73 Dumec Plaza, Area 1, Abuja',
      icoNumber: null,
      email: partner?.support_email || 'clerk@faxter.com',
      complete: true,
      uk,
      partner: false,
      companyNumber: null,
      dpo: null,
      phone: null,
    };
  }
  return {
    name: partner.legal_name || partner.name,
    registration: partner.company_number
      ? uk
        ? `company number ${partner.company_number}`
        : `RC ${partner.company_number}`
      : null,
    address: partner.address || null,
    icoNumber: partner.ico_number || null,
    email: partner.support_email || null,
    complete: Boolean(partner.legal_name && partner.company_number && partner.address),
    uk,
    partner: true,
    companyNumber: partner.company_number || null,
    dpo: partner.dpo || null,
    phone: partner.phone || null,
  };
}

function useEntity(): Entity | null {
  const { config, loaded, fetchConfig } = usePartnerStore();
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  return loaded ? entityFor(config) : null;
}

function Contact({ entity }: { entity: Entity }) {
  return entity.email ? (
    <a href={`mailto:${entity.email}`}>{entity.email}</a>
  ) : (
    <>the support channel in your account</>
  );
}

function Layout({
  title,
  entity,
  effective,
  children,
}: {
  title: string;
  entity: Entity | null;
  /** The effective date, when it depends on whose policy this is. */
  effective?: (e: Entity) => string;
  children: (e: Entity) => ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} — ${APP_NAME}`;
  }, [title]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" className="text-lg font-semibold text-ink">
            {APP_NAME}
          </Link>
          <Link to="/login" className="text-sm text-ink-2 hover:text-ink">
            Log in
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12 sm:py-16 [&_a]:text-brand [&_a]:underline [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-relaxed [&_li]:text-ink-2 [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-ink-2 [&_strong]:text-ink [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
        <h1 className="mb-2 text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="mb-8 text-sm text-ink-3">
          {effective && entity === null
            ? '\u00a0'
            : `Effective ${entity && effective ? effective(entity) : UPDATED}`}
        </p>
        {entity === null ? (
          <div className="space-y-3" aria-hidden>
            <div className="h-4 w-3/4 animate-pulse rounded bg-line" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-line" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-line" />
          </div>
        ) : (
          <>
            {!entity.complete && (
              <div className="mb-8 rounded-lg border border-line bg-surface p-4 text-sm">
                <strong>Draft for review.</strong> This document is a working draft and is pending
                review by legal counsel. It is not yet a binding agreement.
              </div>
            )}
            {children(entity)}
          </>
        )}
      </main>
    </div>
  );
}

function WhoWeAre({ e }: { e: Entity }) {
  return (
    <>
      <strong>{e.name}</strong>
      {e.registration && (
        <>
          , a company registered in {e.uk ? 'England and Wales' : 'Nigeria'} with {e.registration}
        </>
      )}
      {e.address && <>, whose registered office is at {e.address}</>}
    </>
  );
}

function ContactBlock({ e }: { e: Entity }) {
  return (
    <p>
      {e.name}
      {e.address && (
        <>
          <br />
          {e.address}
        </>
      )}
      {e.email && (
        <>
          <br />
          <Contact entity={e} />
        </>
      )}
    </p>
  );
}

export function PrivacyPage() {
  const entity = useEntity();
  return (
    <Layout
      title="Privacy Policy"
      entity={entity}
      effective={(e) => (e.uk && e.partner ? UK_PARTNER_PRIVACY_UPDATED : UPDATED)}
    >
      {(e) => {
        if (e.uk && e.partner) return <UkPartnerPrivacy e={e} />;
        const law = e.uk ? 'the UK GDPR' : 'the NDPA';
        return (
          <>
            <p>
              {APP_NAME} is an AI support desk: it answers your customers on WhatsApp, web chat and
              phone, sorts every conversation, and hands what needs a person to your team. This
              policy explains what personal data we handle to do that, why, how we protect it, and
              the rights people have over it.
            </p>

            <h2>1. Who we are</h2>
            <p>
              {APP_NAME} is provided by <WhoWeAre e={e} /> (&ldquo;we&rdquo;, &ldquo;us&rdquo;). For
              the personal data of people who use {APP_NAME} to run their support (account holders
              and their team) we are the <strong>data controller</strong> under{' '}
              {e.uk
                ? 'the UK General Data Protection Regulation (“UK GDPR”) and the Data Protection Act 2018'
                : 'the Nigeria Data Protection Act 2023 (“NDPA”)'}
              .
              {e.uk && e.icoNumber && (
                <>
                  {' '}
                  We are registered with the Information Commissioner&rsquo;s Office under number{' '}
                  {e.icoNumber}.
                </>
              )}{' '}
              Questions can be sent to us through <Contact entity={e} />.
            </p>

            <h2>2. Your customers&rsquo; conversations</h2>
            <p>
              When a business uses {APP_NAME}, the messages, calls and details of its customers
              belong to that business. For that data the business is the controller and we are its{' '}
              <strong>processor</strong>: we handle it only to provide the service, under the data
              processing terms in our <Link to="/terms">Terms of Service</Link>. If you are a
              customer of a business that uses {APP_NAME}, please contact that business about your
              data; we will help them respond.
            </p>

            <h2>3. What we collect</h2>
            <ul>
              <li>
                <strong>Account data</strong>: name, email address, phone number, role and login
                credentials for you and your team.
              </li>
              <li>
                <strong>Conversations</strong>: messages, attachments and voice notes exchanged on
                the channels you connect, with transcripts we make of voice notes, and call details
                (and recordings, where you switch them on).
              </li>
              <li>
                <strong>Customer and order details</strong>: the names, contact details and order or
                payment status {APP_NAME} looks up in the systems you connect, so it can answer
                questions about them.
              </li>
              <li>
                <strong>Knowledge</strong>: the documents and answers you give {APP_NAME} to work
                from.
              </li>
              <li>
                <strong>Usage and technical data</strong>: device, browser, IP address and how the
                product is used, to run, secure and improve it.
              </li>
            </ul>

            <h2>4. How we use data, and our lawful basis</h2>
            <p>Under {law} we rely on:</p>
            <ul>
              <li>
                <strong>Contract</strong>: to provide the service you signed up for.
              </li>
              <li>
                <strong>Legal obligation</strong>: to keep the records the law requires and respond
                to lawful requests.
              </li>
              <li>
                <strong>Legitimate interests</strong>: to secure, maintain and improve the product
                and prevent misuse, where those interests are not outweighed by people&rsquo;s
                rights.
              </li>
              <li>
                <strong>Consent</strong>: where the law requires it. Consent can be withdrawn at any
                time.
              </li>
            </ul>

            <h2>5. AI and automated replies</h2>
            <p>
              {APP_NAME} uses AI models to draft and send replies, summarise conversations and
              decide which ones need a person. Your team can see every conversation and take over at
              any time, and {APP_NAME} hands over whenever a customer asks for a person. We do not
              make decisions about anyone based solely on automated processing that have legal or
              similarly significant effects.{' '}
              <strong>
                We do not sell data, and we do not use your conversations to train third-party AI
                models.
              </strong>
            </p>

            <h2>6. Who we share data with</h2>
            <ul>
              <li>
                <strong>Service providers (processors)</strong>: hosting, messaging and telephony,
                AI and email providers, acting only on our instructions under written contracts.
              </li>
              <li>
                <strong>The systems you connect</strong>, such as your shop or payment provider,
                when you ask {APP_NAME} to look something up there.
              </li>
              <li>
                <strong>Authorities</strong>, where the law requires it, and professional advisers
                or the parties to a sale of our business, under suitable safeguards.
              </li>
            </ul>

            <h2>7. International transfers</h2>
            <p>
              {e.uk
                ? 'Some of our providers process data outside the UK. Where they do, the destination is covered by UK adequacy regulations, or we use the International Data Transfer Agreement or the UK Addendum to the EU Standard Contractual Clauses, with any additional measures the transfer needs.'
                : 'Some of our providers process data outside Nigeria. Where data is transferred abroad, we take the steps the NDPA requires to keep it adequately protected, including contractual safeguards with the recipients.'}
            </p>

            <h2>8. How long we keep it</h2>
            <p>
              We keep account data while your account is open and for as long afterwards as the law
              requires. Conversations are kept for as long as your account needs them; when your
              account closes we delete them, or return them if you ask, unless the law requires us
              to keep them.
            </p>

            <h2>9. Security</h2>
            <p>
              We encrypt data in transit and at rest, keep each business&rsquo;s data separate from
              every other&rsquo;s, and limit access to those who need it.
            </p>

            <h2>10. Your rights</h2>
            <p>
              Under {law} you have the right to access the personal data we hold about you, to have
              it corrected or erased where the law allows, to restrict or object to processing, to
              receive it in a portable format, and to withdraw consent where we rely on it. Contact
              us through <Contact entity={e} />.{' '}
              {e.uk ? (
                <>
                  We will respond within one month. You can also complain to the Information
                  Commissioner&rsquo;s Office at{' '}
                  <a href="https://ico.org.uk/make-a-complaint/">ico.org.uk</a> or on 0303 123 1113.
                </>
              ) : (
                <>You can also complain to the Nigeria Data Protection Commission.</>
              )}
            </p>

            <h2>11. Cookies</h2>
            <p>
              We use only the cookies and local storage that are strictly necessary to keep you
              signed in and the service secure. We do not use advertising or third-party tracking
              cookies.
            </p>

            <h2>12. Changes to this policy</h2>
            <p>
              If we make significant changes, we will update the effective date above and, where
              appropriate, let you know.
            </p>

            <h2>13. Contact us</h2>
            <ContactBlock e={e} />
          </>
        );
      }}
    </Layout>
  );
}

/**
 * The privacy policy for a UK partner's instance: UK GDPR, the Data Protection
 * Act 2018 and PECR, with the partner's company as data controller. Follows
 * the partner's own policy (Zest I/O's, October 2026, as Clerk has it), with
 * the parts particular to Pulse: your customers' conversations, WhatsApp and
 * Meta, AI replies, and the providers that run the service and where they are.
 */
function UkPartnerPrivacy({ e }: { e: Entity }) {
  const cell = 'border border-line px-3 py-2 align-top text-left text-ink-2';
  return (
    <>
      <h2 id="who-we-are">1. Who we are</h2>
      <p>
        {APP_NAME} is provided by <strong>{e.name}</strong> (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
        &ldquo;our&rdquo;), a UK company.
      </p>
      <ul>
        <li>
          <strong>Company name:</strong> {e.name}
        </li>
        {e.address && (
          <li>
            <strong>Registered address:</strong> {e.address}
          </li>
        )}
        {e.companyNumber && (
          <li>
            <strong>Company number:</strong> {e.companyNumber}
          </li>
        )}
        {e.dpo && (
          <li>
            <strong>Data Protection Officer:</strong> {e.dpo}
          </li>
        )}
        {e.icoNumber && (
          <li>
            <strong>ICO registration number:</strong> {e.icoNumber}
          </li>
        )}
        <li>
          <strong>Contact:</strong> <Contact entity={e} />
          {e.phone && <> · {e.phone}</>}
        </li>
      </ul>
      <p>
        {APP_NAME} is an AI customer-communications service for businesses: it answers a
        business&rsquo;s customers on WhatsApp, web chat and phone, sorts every conversation, and
        hands what needs a person to the business&rsquo;s team. This policy explains how we handle
        personal data when you use {APP_NAME}: the website, the app, and the channels through which{' '}
        {APP_NAME} talks to your customers. Depending on the situation, we act in one of two roles
        under the UK General Data Protection Regulation (&ldquo;UK GDPR&rdquo;) and the Data
        Protection Act 2018:
      </p>
      <ul>
        <li>
          <strong>As a controller:</strong> when we decide how and why personal data is used, for
          example when you visit our website, sign up, contact us or receive our marketing, and for
          the account data of you and your team.
        </li>
        <li>
          <strong>As a processor:</strong> when {APP_NAME} talks to your customers, it handles
          personal data that belongs to your business, such as your customers&rsquo; names, phone
          numbers, messages and orders. Your business is the controller of that data, and our
          handling of it is governed by the data processing terms in our{' '}
          <Link to="/terms">Terms of Service</Link>.
        </li>
      </ul>

      <h2 id="what-we-collect">2. The personal data we collect</h2>
      <p>The data we collect depends on how you use {APP_NAME}. It may include:</p>
      <ul>
        <li>
          <strong>Account data:</strong> your name, work email address, phone number, company name,
          role and login details, and those of the team members you invite.
        </li>
        <li>
          <strong>Billing data:</strong> the billing and invoice details of the people who manage
          the account.
        </li>
        <li>
          <strong>Conversations:</strong> the messages, images, documents, voice notes and calls
          exchanged with your customers on the channels you connect, such as WhatsApp, web chat and
          phone, the transcripts {APP_NAME} makes of voice notes and calls, and call recordings
          where they are switched on.
        </li>
        <li>
          <strong>Customer data:</strong> your customers&rsquo; names, phone numbers and email
          addresses, their past conversations, and what {APP_NAME} notes from them, such as each
          conversation&rsquo;s category, priority and sentiment, and details a customer has told
          you.
        </li>
        <li>
          <strong>Order and catalogue data:</strong> the products, prices, stock and orders, with
          the customer details on them, that {APP_NAME} reads from the shop or other systems you
          connect, such as Shopify or a system you connect through its API, so that it can answer
          questions about them.
        </li>
        <li>
          <strong>Knowledge:</strong> the documents and answers you give {APP_NAME} to work from.
        </li>
        <li>
          <strong>Service and support data:</strong> what you tell us when you ask for help,
          including your messages to our support staff.
        </li>
        <li>
          <strong>Website and usage data:</strong> IP address, browser type, pages visited and the
          referring website.
        </li>
        <li>
          <strong>Marketing data:</strong> your choices about receiving news, invitations and
          updates from us.
        </li>
        <li>
          <strong>Training and event data:</strong> your name, role and contact details if you
          attend a training session, event or webinar.
        </li>
      </ul>
      <p>
        We do not normally collect special category data (such as health, religious or ethnic
        information). If our services ever need it, we will explain why and obtain your explicit
        consent, or rely on another lawful basis, before doing so. Your customers may still share it
        in a conversation, for example health details with a clinic; that data belongs to your
        business, and we handle it only as your processor.
      </p>

      <h2 id="how-we-use">3. How and why we use your data</h2>
      <p>
        We only use personal data where we have a lawful basis to do so. The main purposes and their
        bases are:
      </p>
      <table className="mb-4 w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={`${cell} font-semibold text-ink`}>Purpose</th>
            <th className={`${cell} font-semibold text-ink`}>Lawful basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={cell}>
              Providing {APP_NAME}, including hosting, backups, support, setting up your account and
              connecting your channels
            </td>
            <td className={cell}>Performance of a contract with you</td>
          </tr>
          <tr>
            <td className={cell}>
              Managing our relationship with you, including invoicing and account administration
            </td>
            <td className={cell}>
              Performance of a contract; compliance with a legal obligation (tax and accounting)
            </td>
          </tr>
          <tr>
            <td className={cell}>
              Responding to enquiries made through our website, email or telephone
            </td>
            <td className={cell}>Legitimate interests (responding to business enquiries)</td>
          </tr>
          <tr>
            <td className={cell}>
              Sending service updates, security notices and information you have asked for
            </td>
            <td className={cell}>Legitimate interests; consent where required</td>
          </tr>
          <tr>
            <td className={cell}>Sending marketing about our services</td>
            <td className={cell}>
              Legitimate interests, or consent where the law requires it, which you can withdraw at
              any time
            </td>
          </tr>
          <tr>
            <td className={cell}>
              Improving {APP_NAME}, our website and security, and protecting against fraud and
              misuse
            </td>
            <td className={cell}>Legitimate interests</td>
          </tr>
          <tr>
            <td className={cell}>Complying with legal and regulatory obligations</td>
            <td className={cell}>Compliance with a legal obligation</td>
          </tr>
        </tbody>
      </table>
      <p>
        Where we rely on legitimate interests, we have balanced our interests against your rights
        and expectations, and we do not use your data in a way you would not reasonably expect.
      </p>

      <h2 id="marketing">4. Marketing</h2>
      <p>
        We only send marketing by email where you have a legitimate interest in it, or you are an
        existing customer and the message is about services like the ones you already use. Every
        marketing email has an unsubscribe link, and you can opt out at any time by contacting{' '}
        <Contact entity={e} />. We keep a suppression list so that we do not contact people who have
        opted out.
      </p>

      <h2 id="whatsapp-and-ai">5. WhatsApp, Meta and AI</h2>
      <p>
        When you connect a WhatsApp number, your customers&rsquo; messages, and the replies{' '}
        {APP_NAME} and your team send, pass through WhatsApp, which is operated by Meta Platforms.
        Meta&rsquo;s own terms and privacy policy govern its handling of them. Connecting a WhatsApp
        Business account with Facebook uses Meta&rsquo;s sign-up, so the WhatsApp settings page, and
        only that page, loads Meta&rsquo;s software (see section 11). You can disconnect a number at
        any time, and we then stop receiving its messages.
      </p>
      <p>
        {APP_NAME} uses AI models to read and reply to your customers&rsquo; messages, transcribe
        voice notes and calls, read images such as payment receipts, summarise and sort
        conversations, and decide which of them need a person. Your team can see every conversation
        and take over at any time, and {APP_NAME} hands over whenever a customer asks for a person.
        We do not make decisions about anyone based solely on automated processing that have legal
        or similarly significant effects. <strong>We never sell personal data</strong>, and we do
        not use your conversations to train third-party AI models.
      </p>

      <h2 id="sharing">6. Who we share your data with</h2>
      <p>We share personal data only where necessary, and only with:</p>
      <ul>
        <li>
          <strong>Service providers that run {APP_NAME}:</strong> hosting and database servers, file
          storage and backups, the AI model providers described in section 5, email delivery, and
          the telephony providers that carry calls. They act only on our instructions, under written
          contracts.
        </li>
        <li>
          <strong>Meta (WhatsApp)</strong>, which carries WhatsApp messages between your business
          and its customers, as described in section 5.
        </li>
        <li>
          <strong>The systems you connect</strong>, such as your shop or CRM, when {APP_NAME} looks
          something up there or records a conversation there for you.
        </li>
        <li>
          <strong>Your team</strong>: the colleagues you invite to your account.
        </li>
        <li>
          <strong>Professional advisers and subcontractors:</strong> accountants, auditors, insurers
          and legal advisers, where they work with us on our business or a customer&rsquo;s.
        </li>
        <li>
          <strong>Authorities:</strong> HM Revenue &amp; Customs, regulators, law enforcement or
          courts, where we are legally required or permitted to disclose.
        </li>
      </ul>
      <p>Any disclosure is limited to the minimum necessary for the purpose.</p>

      <h2 id="transfers">7. Where your data is kept</h2>
      <p>
        {APP_NAME}&rsquo;s servers and databases are in the European Economic Area. Some of our
        service providers process data outside the UK and the EEA, including in the United States
        and China. Where they do, we protect the transfer as UK law requires: the destination is
        covered by UK adequacy regulations, or we rely on the International Data Transfer Agreement
        or the UK Addendum to the EU Standard Contractual Clauses.
      </p>

      <h2 id="security">8. How we protect your data</h2>
      <p>
        We maintain technical and organisational measures appropriate to the nature of the data and
        the harm its loss or misuse could cause. These include:
      </p>
      <ul>
        <li>
          <strong>Access control:</strong> server and database access is limited to authorised staff
          and protected by strong authentication. Passwords are never shared.
        </li>
        <li>
          <strong>Least privilege:</strong> people are given access to personal data only where
          their role needs it, and that access is monitored.
        </li>
        <li>
          <strong>Separation:</strong> each business&rsquo;s conversations and records are kept
          apart from every other business&rsquo;s.
        </li>
        <li>
          <strong>Encryption:</strong> data is encrypted in transit.
        </li>
        <li>
          <strong>Audit trail:</strong> the requests {APP_NAME} makes to the systems you connect are
          logged.
        </li>
        <li>
          <strong>Staff obligations:</strong> everyone who handles personal data is bound by
          confidentiality and our security procedures.
        </li>
        <li>
          <strong>Backups:</strong> data is backed up securely, and backups are kept separately from
          the originals.
        </li>
        <li>
          <strong>Breach management:</strong> we have procedures to detect, investigate and remedy
          security incidents. Where a personal data breach is likely to put individuals at risk, we
          tell the Information Commissioner&rsquo;s Office within 72 hours, and the people affected
          where required.
        </li>
      </ul>
      <p>
        If you believe your data has been compromised while using {APP_NAME}, contact us straight
        away at <Contact entity={e} />.
      </p>

      <h2 id="retention">9. How long we keep your data</h2>
      <p>
        We keep personal data only for as long as we need it for the purposes in this policy, or as
        long as the law requires:
      </p>
      <ul>
        <li>
          <strong>Customer and financial records:</strong> for the period UK tax and company law
          requires, currently six years after the end of the financial year they relate to.
        </li>
        <li>
          <strong>Enquiry and marketing data:</strong> until you unsubscribe or ask us to delete it.
        </li>
        <li>
          <strong>Data we process for your business:</strong> your customers&rsquo; conversations
          and details are kept for as long as your account needs them. At the end of your
          subscription, we return or securely delete them as you instruct, and delete other copies,
          including backups, unless the law requires us to keep them.
        </li>
        <li>
          <strong>Suppression and complaint records:</strong> for as long as we need them to respect
          your rights and protect our legal position.
        </li>
      </ul>

      <h2 id="your-rights">10. Your rights</h2>
      <p>Under the UK GDPR and the Data Protection Act 2018, you have the right to:</p>
      <ul>
        <li>
          <strong>Access:</strong> obtain a copy of the personal data we hold about you.
        </li>
        <li>
          <strong>Rectification:</strong> have inaccurate data corrected or incomplete data
          completed.
        </li>
        <li>
          <strong>Erasure:</strong> ask us to delete your personal data in certain circumstances.
        </li>
        <li>
          <strong>Restriction:</strong> ask us to limit how we use your data in certain
          circumstances.
        </li>
        <li>
          <strong>Portability:</strong> receive your data in a structured, commonly used,
          machine-readable format, where we process it by digital means on the basis of consent or
          contract.
        </li>
        <li>
          <strong>Objection:</strong> object to processing based on our legitimate interests, and
          object at any time to direct marketing.
        </li>
        <li>
          <strong>Withdraw consent:</strong> where we rely on consent, withdraw it at any time. This
          does not affect processing that took place before you withdrew it.
        </li>
        <li>
          <strong>Complain:</strong> raise a concern with us, or with the Information
          Commissioner&rsquo;s Office.
        </li>
      </ul>
      <p>
        To use any of these rights, contact us at <Contact entity={e} />. We will respond within one
        month, and we may need to verify your identity first. If you are a customer of a business
        that uses {APP_NAME}, for example you messaged the business on WhatsApp and {APP_NAME}{' '}
        replied, please contact that business, which is the controller of your data; we will help
        them respond.
      </p>

      <h2 id="cookies">11. Cookies and similar technologies</h2>
      <p>
        {APP_NAME} keeps what it needs to keep you signed in, secure and as you left it, such as
        your sign-in session and display settings, in your browser&rsquo;s local storage. This is
        strictly necessary and does not need your consent. We do not use advertising or analytics
        cookies. Meta&rsquo;s software (the Facebook SDK), which connecting a WhatsApp Business
        account with Facebook needs, is loaded only on the WhatsApp settings page, whenever you open
        it, so that you can connect WhatsApp; no other page loads it. Once it has loaded, Meta may
        set its own cookies, under Meta&rsquo;s cookie policy. Our pages load their fonts from
        Google Fonts, which receives your IP address to deliver them. You can manage cookies and
        site data through your browser settings.
      </p>

      <h2 id="complaints">12. Complaints</h2>
      <p>
        We would like the chance to resolve any concern first, so please contact us at{' '}
        <Contact entity={e} />. You also have the right to complain to the Information
        Commissioner&rsquo;s Office (ICO):
      </p>
      <ul>
        <li>
          <strong>Website:</strong> <a href="https://ico.org.uk/make-a-complaint/">ico.org.uk</a>
        </li>
        <li>
          <strong>Telephone:</strong> 0303 123 1113
        </li>
      </ul>
      {e.icoNumber && (
        <p>We are registered with the ICO as a data controller, reference number {e.icoNumber}.</p>
      )}

      <h2 id="changes">13. Changes to this policy</h2>
      <p>
        We may update this policy to reflect changes in our services, the law or best practice.
        Where a change materially affects your rights, we will tell you directly where we hold your
        contact details.
      </p>

      <h2 id="contact">14. How to contact us</h2>
      <p>
        Questions, requests or concerns about this policy or how we handle your personal data can be
        sent to:
      </p>
      <p>
        {e.dpo && (
          <>
            {e.dpo}, Data Protection Officer
            <br />
          </>
        )}
        {e.name}
        {e.address && <>, {e.address}</>}
        <br />
        Email: <Contact entity={e} />
        {e.phone && <> · Telephone: {e.phone}</>}
      </p>
    </>
  );
}

export function TermsPage() {
  const entity = useEntity();
  return (
    <Layout title="Terms of Service" entity={entity}>
      {(e) => (
        <>
          <h2>1. Agreement to these terms</h2>
          <p>
            These Terms of Service (the &ldquo;Terms&rdquo;) are a binding agreement between you, or
            the business you represent (&ldquo;you&rdquo;), and <WhoWeAre e={e} />{' '}
            (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using {APP_NAME} you
            accept these Terms. If you accept them for a business, you confirm you have authority to
            bind it. {APP_NAME} is for business use only.
          </p>

          <h2>2. The service</h2>
          <p>
            {APP_NAME} answers your customers on the channels you connect, using AI and the
            knowledge you give it, and hands conversations to your team when they need a person. AI
            replies can be wrong. You are responsible for the knowledge and instructions you give{' '}
            {APP_NAME}, for supervising its replies, and for what your business promises its
            customers.
          </p>

          <h2>3. Your account</h2>
          <p>
            You must be at least 18. You are responsible for your team&rsquo;s logins and for all
            activity under your account. Tell us promptly through <Contact entity={e} /> if you
            suspect someone else is using it.
          </p>

          <h2>4. Acceptable use</h2>
          <p>
            You will use {APP_NAME} lawfully, have a lawful basis to message the people you contact,
            and follow the rules of the messaging platforms you connect (including WhatsApp&rsquo;s
            Business and Commerce policies). You will not use it to send spam, or for any fraudulent
            or harmful purpose.
          </p>

          <h2>5. Fees and payment</h2>
          <p>
            The fees for your plan are set out at sign-up or in your order
            {e.uk ? ', and are exclusive of VAT, which we add at the applicable rate' : ''}. Unless
            stated otherwise they are billed monthly in advance and are non-refundable except where
            the law requires. We may change our fees on at least 30 days&rsquo; notice, taking
            effect from your next billing period. Messaging and telephony charges from third-party
            providers may be billed separately.
          </p>

          <h2>6. Third-party services</h2>
          <p>
            {APP_NAME} works with third parties such as WhatsApp, telephony providers, shops and
            payment providers. Your use of them is also governed by their terms, and we are not
            responsible for their availability or conduct.
          </p>

          <h2>7. Intellectual property</h2>
          <p>
            We, or our licensors, own the {APP_NAME} software and brand. You own your data and your
            conversations, and you give us the rights we need to process them to provide the
            service.
          </p>

          <h2>8. Data protection</h2>
          <p>
            Each of us will comply with{' '}
            {e.uk ? 'the UK GDPR and the Data Protection Act 2018' : 'the NDPA'}. Our{' '}
            <Link to="/privacy">Privacy Policy</Link> explains how we handle personal data as a
            controller. For your customers&rsquo; data you are the controller and we are your
            processor, and we will: process it only on your documented instructions, which these
            Terms record; bind everyone who handles it to confidentiality; keep it secure; use
            sub-processors only under contracts giving the same protection, telling you before we
            add or replace one; help you respond to people&rsquo;s requests; tell you without undue
            delay of a personal data breach; delete or return it when the service ends unless the
            law requires us to keep it; and give you the information you reasonably need to show
            these obligations are met.
          </p>

          <h2>9. Term and termination</h2>
          <p>
            You may cancel at any time; access continues until the end of the period you have paid
            for. We may suspend or end your account for material breach, non-payment or unlawful
            use. You may ask for an export of your data within 30 days of your account ending.
          </p>

          <h2>10. Liability</h2>
          <p>
            Except as these Terms say, and to the extent the law allows, the service is provided
            &ldquo;as is&rdquo;. Nothing in these Terms limits liability that cannot lawfully be
            limited
            {e.uk
              ? ', including for death or personal injury caused by negligence, or for fraud'
              : ''}
            . Subject to that, we are not liable for indirect or consequential loss, or for loss of
            profit, revenue, goodwill or data, and our total liability is limited to the fees you
            paid us in the 12 months before the claim arose.
          </p>

          <h2>11. Changes</h2>
          <p>
            We may update the service and these Terms. For significant changes we will update the
            effective date above and give you reasonable notice; using {APP_NAME} after a change
            takes effect means you accept it.
          </p>

          <h2>12. General and governing law</h2>
          <p>
            These Terms and our Privacy Policy are the entire agreement between us about the
            service. If any part is unenforceable, the rest stays in force.{' '}
            {e.uk
              ? 'No one else has any right under the Contracts (Rights of Third Parties) Act 1999 to enforce these Terms. They are governed by the law of England and Wales, and the courts of England and Wales have exclusive jurisdiction.'
              : 'These Terms are governed by the laws of the Federal Republic of Nigeria, and the Nigerian courts have jurisdiction over any dispute we cannot resolve informally.'}
          </p>

          <h2>13. Contact us</h2>
          <ContactBlock e={e} />
        </>
      )}
    </Layout>
  );
}
